import { CustomerType, PaymentStatus } from '@repo/common';
import { useDebounce } from '@uidotdev/usehooks';
import { FC, PropsWithChildren, useState } from 'react';

import { Option } from '@/components/ui/autocomplete/auto-complete-input';
import { ResetFilterButton } from '@/components/ui/button';
import {
    AutoCompleteFilter,
    SelectFilter,
    TextSearchFilter,
} from '@/components/ui/search-filter';
import { useGetCustomersQuery } from '@/features/slice/customers/customer-api-slice';
import { useGetUsersQuery } from '@/features/slice/users';
import { useUrlFilter } from '@/hooks';

import {
    CUSTOMER_ID_FILTER_KEY,
    CUSTOMER_NAME_FILTER_KEY,
    CUSTOMER_TYPE_FILTER_KEY,
    PAYMENT_STATUS_FILTER_KEY,
    USER_ID_FILTER_KEY,
    USER_NAME_FILTER_KEY,
} from './constant';

type OrderHeadingActionsProps = PropsWithChildren;

export const OrderHeadingActions: FC<OrderHeadingActionsProps> = ({
    children,
}) => {
    const { setSearchText, setSearchFilter, getSearchFilter, searchText } =
        useUrlFilter();

    const [customerSearchText, setCustomerSearchText] = useState('');
    const [userSearchText, setUserSearchText] = useState('');

    const debouncedCustomerSearchText = useDebounce(customerSearchText, 500);
    const debouncedUserSearchText = useDebounce(userSearchText, 500);

    const {
        data: customersData,
        isLoading: isCustomerLoading,
        isFetching: isCustomerFetching,
    } = useGetCustomersQuery({
        search: debouncedCustomerSearchText,
    });
    const {
        data: usersData,
        isLoading: isUserLoading,
        isFetching: isUserFetching,
    } = useGetUsersQuery({
        search: debouncedUserSearchText,
    });

    const customers = customersData?.data?.map((customer) => ({
        label: customer.name,
        value: customer.id,
    }));

    const users = usersData?.data?.map((user) => ({
        label: user.name as string,
        value: user.id,
    }));

    const handleSelectCustomer = (selectedValue: Option) => {
        setSearchFilter(CUSTOMER_ID_FILTER_KEY, selectedValue.value);
        setSearchFilter(CUSTOMER_NAME_FILTER_KEY, selectedValue.label);
    };

    const handleSelectUser = (selectedValue: Option) => {
        setSearchFilter(USER_ID_FILTER_KEY, selectedValue.value);
        setSearchFilter(USER_NAME_FILTER_KEY, selectedValue.label);
    };

    const customerTypeFilter = getSearchFilter(CUSTOMER_TYPE_FILTER_KEY) || '';
    const paymentStatusFilter =
        getSearchFilter(PAYMENT_STATUS_FILTER_KEY) || '';
    const customerIdFilter = getSearchFilter(CUSTOMER_ID_FILTER_KEY) || '';
    const customerNameFilter = getSearchFilter(CUSTOMER_NAME_FILTER_KEY) || '';
    const userIdFilter = getSearchFilter(USER_ID_FILTER_KEY) || '';
    const userNameFilter = getSearchFilter(USER_NAME_FILTER_KEY) || '';

    const customerValue = {
        label: customerNameFilter,
        value: customerIdFilter,
    };

    const userValue = {
        label: userNameFilter,
        value: userIdFilter,
    };

    return (
        <div className="flex flex-col flex-wrap items-center justify-start gap-4 sm:flex-row sm:items-center">
            <TextSearchFilter
                onChangeSearch={(e) => setSearchText(e.target.value)}
                placeholder="order id"
                value={searchText}
            />
            <SelectFilter
                // title="Customer Type"
                placeholder="Customer Type"
                onChange={(value) =>
                    setSearchFilter(CUSTOMER_TYPE_FILTER_KEY, value)
                }
                value={customerTypeFilter}
                options={[
                    { label: 'Retail', value: CustomerType.RETAIL },
                    { label: 'Wholesale', value: CustomerType.WHOLESALE },
                ]}
            />

            <SelectFilter
                // title="Status"
                placeholder="Payment Status"
                onChange={(value) =>
                    setSearchFilter(PAYMENT_STATUS_FILTER_KEY, value)
                }
                value={paymentStatusFilter}
                options={[
                    { label: 'Paid', value: PaymentStatus.PAID },
                    { label: 'Due', value: PaymentStatus.DUE },
                ]}
            />

            <AutoCompleteFilter
                options={customers ?? []}
                value={customerValue}
                onValueChange={handleSelectCustomer}
                placeholder="Select Customer"
                isLoading={isCustomerLoading || isCustomerFetching}
                onInputChange={(value) => setCustomerSearchText(value)}
            />

            <AutoCompleteFilter
                options={users ?? []}
                value={userValue}
                onValueChange={handleSelectUser}
                placeholder="Select User"
                isLoading={isUserLoading || isUserFetching}
                onInputChange={(value) => setUserSearchText(value)}
            />

            {children}

            <ResetFilterButton />
        </div>
    );
};

OrderHeadingActions.displayName = 'OrderheadingActions';
